import { WorldPoint, Direction, Route, opposite, route_from_json } from './grid';
import { Building, Parcel } from './building';
import { BuildingFinder } from './enemy';
import { Distributor } from './graph';
import { Purse, Resource } from './resources';
import { JsonDictionaryWrapper } from './utils';

type CarrierState = {
    // how many ticks through the step
    ticks: number 

    // what step is it on?
    step_index: number 

    // where did it start this step
    last_pt: WorldPoint

    // when true, it is going back
    reversed: boolean

    // what is this carrying?
    carried: Parcel | null
}

function carrier_state_from_json(obj: JsonDictionaryWrapper): CarrierState {
    let carried: Parcel | null = null
    if (obj.exists("carried")) {
        carried = Parcel.create_json(obj.get_object("carried"))
    }

    return {
        ticks: obj.get_number("ticks"),
        step_index: obj.get_number("step_index"),
        last_pt: WorldPoint.create_json(obj.get_object("last_pt")),
        reversed: obj.get_boolean("reversed"),
        carried: carried,
    }
}

export type CarrierVisualState = {
    proportion: number 
    source: WorldPoint
    destination: WorldPoint
    carried: Parcel | null
}

export function cost_for_road(): Purse {
    var p = new Purse(false)
    p.add_resource(1, Resource.PavingSlab)
    return p
}

export class Road {
    public route: Route
    public source: Building
    public destination: Building

    private carrier: CarrierState

    public static max_length: number = 20

    private toJSON(): any {
        return {
            route: this.route,
            source: this.source.position,
            destination: this.destination.position,
            carrier: this.carrier,
        }
    }

    // the number of ticks to traverse an edge
    static carrier_tick_count = 60

    public static create_json(obj: JsonDictionaryWrapper, bf: BuildingFinder): Road {
        const source = bf.find_building(WorldPoint.create_json(obj.get_object("source")))
        if (source == null) {
            throw new Error("Source not found")
        }
        const destination = bf.find_building(WorldPoint.create_json(obj.get_object("destination")))
        if (destination == null) {
            throw new Error("Destination not found")
        }
        const route = route_from_json(obj.get_object("route"))
        let r = new Road(source, destination, route)
        r.carrier = carrier_state_from_json(obj.get_object("carrier"))
        return r
    }

    public get length(): number {
        return this.route.directions.length
    }

    public will_delete(): void {
        if (this.carrier.carried != null) {
            this.carrier.carried.arrived()
        }
    }

    constructor(source: Building, destination: Building, rt: Route) {
        this.source = source
        this.destination = destination
        this.route = rt

        this.carrier = {
            ticks: 0,
            step_index: 0,
            last_pt: source.position,
            reversed: false,
            carried: null
        }
    }

    public visual_state(): CarrierVisualState {
        if (this.carrier.reversed) {
            return {
                proportion: this.carrier.ticks / Road.carrier_tick_count,
                source: this.carrier.last_pt.translated(this.route.directions[this.carrier.step_index]),
                destination: this.carrier.last_pt,
                carried: this.carrier.carried,
            }
        } else {
            return {
                proportion: this.carrier.ticks / Road.carrier_tick_count,
                source: this.carrier.last_pt,
                destination: this.carrier.last_pt.translated(this.route.directions[this.carrier.step_index]),
                carried: this.carrier.carried,
            }
        }
    }

    public get start(): WorldPoint {
        return this.source.position
    }

    private carrier_arrived(bf: BuildingFinder, dis: Distributor): void {
        var location: Building
        var destination: Building
        if (this.carrier.reversed) {
            location = this.source
            destination = this.destination
        } else {
            location = this.destination
            destination = this.source
        }

        if (this.carrier.carried != null) {
            location.drop(this.carrier.carried)
        }

        if (this.carrier.reversed) {
            this.carrier = {
                ticks: 0,
                step_index: 0,
                last_pt: this.source.position,
                reversed: false,
                carried: null,
            }
        } else {
            this.carrier = {
                ticks: 0,
                step_index: this.route.directions.length - 1,
                last_pt: this.destination.position.translated(opposite(this.route.directions[this.route.directions.length - 1])),
                reversed: true,
                carried: null,
            }
}
        this.carrier.carried = location.pickup(destination, dis, bf)
    }

    public tick(bf: BuildingFinder, distributor: Distributor): void {
        this.carrier.ticks += 1
        if (this.carrier.ticks == Road.carrier_tick_count) {
            this.carrier.ticks = 0

            if (this.carrier.reversed) {
                this.carrier.step_index -= 1
                this.carrier.last_pt = this.carrier.last_pt.translated(opposite(this.route.directions[this.carrier.step_index]))
                if (this.carrier.step_index < 0) {
                    this.carrier_arrived(bf, distributor)
                }
            } else {
                this.carrier.last_pt = this.carrier.last_pt.translated(this.route.directions[this.carrier.step_index])
                this.carrier.step_index += 1
                if (this.carrier.step_index == this.route.directions.length) {
                    this.carrier_arrived(bf, distributor)
                }
            }
        }
    }

    public positions(): WorldPoint[] {
        var r: WorldPoint[] = []
        var wp = this.start.copy()
        r.push(wp.copy())
        for (var d of this.route.directions) {
            wp.translate(d)
            r.push(wp.copy())
        }
        return r
    }

    public log(): void {
        console.log("Start route at ", this.start)
        var wp = this.start.copy();
        for (var d of this.route.directions) {
            wp.translate(d)
            console.log("Direction", d, "to", wp.x, ", ", wp.y)
        }
    }
}

